import type { RawMessage } from "@/api/messages";
import { Button } from "@penmate/react-shared/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { letterHasRecoveryOffer } from "@/checkout/recovery";
import { CheckIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { optionCardClass } from "@/components/checkout/optionCard";
import { useAutoAnimate } from "@formkit/auto-animate/react";

export type PhotoTypeSelection = "regular" | "glossy" | "none";

const toLowerString = (value: unknown): string => {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
};

const getPhotoType = (letter: RawMessage | null): string => {
  const v = (letter as any)?.photoType ?? (letter as any)?.photo_type ?? (letter as any)?.data?.photoType ?? (letter as any)?.data?.photo_type;
  const s = toLowerString(v);
  if (s === "3x5") return "glossy";
  return s;
};

const getSelectedPhotosCount = (letter: RawMessage | null): number => {
  const v = (letter as any)?.selectedPhotos ?? (letter as any)?.data?.selectedPhotos ?? (letter as any)?.photos;
  return Array.isArray(v) ? v.length : 0;
};

const getInitialSelection = (letter: RawMessage | null, noPhotosSelected: boolean): PhotoTypeSelection => {
  if (noPhotosSelected && !letterHasRecoveryOffer(letter)) return "none";
  const pt = getPhotoType(letter);
  if (pt === "glossy" && !letterHasRecoveryOffer(letter)) return "glossy";
  return "regular";
};

export function PhotoTypeSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checkoutLetter: RawMessage | null;
  noPhotosEligible: boolean;
  noPhotosSelected: boolean;
  onSave: (args: { selection: PhotoTypeSelection }) => Promise<void>;
}) {
  const { open, onOpenChange, checkoutLetter, noPhotosEligible, noPhotosSelected, onSave } = props;
  const [optionsAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();

  const selectedPhotosCount = useMemo(() => getSelectedPhotosCount(checkoutLetter), [checkoutLetter]);
  const showNoPhotosOption =
    noPhotosEligible && (selectedPhotosCount > 0 || noPhotosSelected) && !letterHasRecoveryOffer(checkoutLetter);
  // An accepted recovery offer prices standard photos only: glossy would
  // route the letter back onto the credits path, and "No photos" would make
  // it free-first-coverable - the server then un-applies the offer at
  // submit, a confusing mid-checkout price flip this sheet must not invite.
  const glossyAvailable = !letterHasRecoveryOffer(checkoutLetter);

  const [selection, setSelection] = useState<PhotoTypeSelection>(
    getInitialSelection(checkoutLetter, noPhotosSelected),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelection(getInitialSelection(checkoutLetter, noPhotosSelected));
    setIsSaving(false);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, (checkoutLetter as any)?.id, noPhotosSelected]);

  const save = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({ selection });
      onOpenChange(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
      setIsSaving(false);
    }
  };

  return (
    // Disable Radix scroll-lock: it can prevent scrolling in fixed/fullscreen overlays on mobile WebViews.
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="pm-sheet w-[calc(100vw-1rem)] max-w-md !p-0 overflow-hidden">
        <DialogTitle className="sr-only">Photo type</DialogTitle>
        <DialogDescription className="sr-only">Choose photo print type.</DialogDescription>

        <div className="flex min-h-0 flex-col">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Photo type</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Choose how photos are printed.
              </div>
            </div>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
            }}
          >
            <div className="grid grid-cols-1 gap-2" ref={optionsAutoAnimateRef}>
              <button
                type="button"
                className={optionCardClass({ selected: selection === "regular" })}
                onClick={() => setSelection("regular")}
                data-testid="photo-type-option-regular"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">Regular</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">Standard prints</div>
                  </div>
                  {selection === "regular" ? (
                    <span className="mt-0.5 text-emerald-600">
                      <CheckIcon className="size-4" aria-hidden="true" />
                    </span>
                  ) : null}
                </div>
              </button>

              {glossyAvailable ? (
                <button
                  type="button"
                  className={optionCardClass({ selected: selection === "glossy" })}
                  onClick={() => setSelection("glossy")}
                  data-testid="photo-type-option-glossy"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">Glossy</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">Photo paper finish</div>
                    </div>
                    {selection === "glossy" ? (
                      <span className="mt-0.5 text-emerald-600">
                        <CheckIcon className="size-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </div>
                </button>
              ) : null}

              {showNoPhotosOption ? (
                <button
                  type="button"
                  className={optionCardClass({ selected: selection === "none" })}
                  onClick={() => setSelection("none")}
                  data-testid="photo-type-option-none"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">No Photos</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">Send text only</div>
                    </div>
                    {selection === "none" ? (
                      <span className="mt-0.5 text-emerald-600">
                        <CheckIcon className="size-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </div>
                </button>
              ) : null}
            </div>

            {saveError ? <div className="mt-3 text-xs text-destructive">{saveError}</div> : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={isSaving}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              onClick={() => void save()}
              disabled={isSaving}
              data-testid="photo-type-save"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
